import { useCallback, useState } from "react";
import { verifyEmail } from "./emailValidation";

/**
 * Hook partilhado para validar emails nos formulários.
 *
 *   const email = useEmailCheck();
 *   ...
 *   <input onBlur={(e) => email.check(e.target.value)} />
 *   <EmailHint state={email} onUseSuggestion={(v) => setEmail(v)} />
 *   // no submit: const r = await email.check(valor); if (!r.valid) return;
 *
 * `mode="warn"` nunca marca como inválido (só avisa) — usado nos ecrãs
 * internos do CRM, onde o consultor pode estar a escrever o que o cliente
 * ditou ao telefone.
 */
export function useEmailCheck({ mode = "block" } = {}) {
  const [state, setState] = useState({ status: "idle", message: "", suggestion: "" });
  const [checking, setChecking] = useState(false);

  const check = useCallback(async (email) => {
    const value = String(email || "").trim();
    if (!value) {
      setState({ status: "idle", message: "", suggestion: "" });
      return { valid: true, message: "", suggestion: "" };
    }
    setChecking(true);
    const r = await verifyEmail(value);
    setChecking(false);
    if (!r.valid) {
      setState({
        status: mode === "warn" ? "warn" : "error",
        message: r.message,
        suggestion: r.suggestion || "",
      });
      return { ...r, valid: mode === "warn" ? true : false, blocked: mode !== "warn" };
    }
    setState({
      status: r.suggestion ? "warn" : "ok",
      message: r.suggestion ? `Quis dizer ${r.suggestion}?` : "",
      suggestion: r.suggestion || "",
    });
    return r;
  }, [mode]);

  const reset = useCallback(() => {
    setState({ status: "idle", message: "", suggestion: "" });
  }, []);

  return { ...state, checking, check, reset };
}

/** Mensagem inline (erro vermelho ou aviso âmbar) + atalho de correcção. */
export function EmailHint({ state, onUseSuggestion, testid = "email-hint" }) {
  if (!state || state.status === "idle" || state.status === "ok") return null;
  const isError = state.status === "error";
  const text = state.message || (state.suggestion ? `Quis dizer ${state.suggestion}?` : "");
  if (!text) return null;
  return (
    <p
      data-testid={testid}
      role={isError ? "alert" : undefined}
      className={`mt-1 text-xs font-semibold flex items-start gap-1.5 ${isError ? "text-[#DC2626]" : "text-[#B45309]"}`}>
      <span className={`w-4 h-4 rounded-full text-white text-[10px] grid place-items-center shrink-0 ${isError ? "bg-[#DC2626]" : "bg-[#D97706]"}`}>!</span>
      <span>
        {text}
        {state.suggestion && onUseSuggestion && (
          <button type="button" data-testid={`${testid}-use`}
            onClick={() => onUseSuggestion(state.suggestion)}
            className="ml-1.5 underline decoration-dotted underline-offset-2 font-bold bg-transparent border-0 p-0 cursor-pointer hover:decoration-solid"
            style={{ color: "inherit", font: "inherit" }}>
            usar {state.suggestion}
          </button>
        )}
      </span>
    </p>
  );
}
